import React, { useEffect, useState } from 'react'
import moment from 'moment'
import { useParams } from 'react-router-dom'
import {
    fetchEmployeeLeave,
    fetchEmployeeLeaveBalance,
    fetchLeaveDetails,
    updateApprovedByLeave,
    updateCheckedByLeave,
    updateFinalApprovedByLeave,
    updateLeaveDeclined,
    verifyLeave
} from '../../../services/LeaveService'
import LeaveEditModal from './LeaveEditModal'

const API_MAP = {
    checker: updateCheckedByLeave,
    first_approver: updateApprovedByLeave,
    final_approver: updateFinalApprovedByLeave,
    declined: updateLeaveDeclined
}

const LeaveDetails = () => {
    const { leaveId } = useParams()
    const [leave, setLeave] = useState(null)
    const [loading, setLoading] = useState(true)
    const [message, setMessage] = useState({ type: '', text: '', isOpen: false })
    const [modalState, setModalState] = useState({ isOpen: false, action: '' })
    const [declineReason, setDeclineReason] = useState('')
    const [leaveData, setLeaveData] = useState({ vacationLeave: 0, sickLeave: 0, emergencyLeave: 0 })
    const [editModalOpen, setEditModalOpen] = useState(false)

    useEffect(() => {
        fetchData()
    }, [leaveId])

    const fetchEmployeeList = async (leaveId) => {
        try {
            const filter = { leaveId }
            let response = await fetchEmployeeLeaveBalance(filter)

            setLeaveData({
                vacationLeave: response.vacationLeave || 0,
                sickLeave: response.sickLeave || 0,
                emergencyLeave: response.emergencyLeave || 0
            })
        } catch (error) {
            console.error('Error fetching fetchEmployeeList:', error)
        } finally {
        }
    }

    const fetchData = async () => {
        try {
            const data = await fetchLeaveDetails(leaveId)
            if (!data?.leave) throw new Error('Leave not found')

            setLeave({
                ...data.leave,
                extendedProps: {
                    fullname: `${data.leave.employee?.firstName || 'N/A'} ${data.leave.employee?.lastName || 'N/A'}`,
                    description: data.leave.reason || 'No description',
                    status: data.leave.status || 'Unknown',
                    duration: data.extendedProps.duration || 'N/A'
                }
            })
            fetchEmployeeList(data.leave.employee.id)
        } catch (error) {
            console.error(error)
            setMessage({ type: 'error', text: error.message || 'Failed to fetch leave details.', isOpen: true })
        } finally {
            setLoading(false)
        }
    }

    const handleStatusChange = async (status) => {
        if (!leave?.id) return setErrorMessage('Leave request does not exist.')
        if (status === 'DECLINED' && !declineReason) return setErrorMessage('Please enter a reason for declining.')

        setLoading(true)
        try {
            const request = {
                id: leave.id,
                remarks: status === 'DECLINED' ? declineReason : 'Approved',
                status
            }
            const response = await verifyLeave(request)
            setMessage({ type: 'success', text: response.message, isOpen: true })
            resetModal()
            fetchData()
        } catch (error) {
            console.error(error)
            setErrorMessage(error.message || 'Failed to update leave status.')
        } finally {
            setLoading(false)
        }
    }

    const setErrorMessage = (text) => setMessage({ type: 'Failed', text, isOpen: true })
    const resetModal = () => {
        setModalState({ isOpen: false, action: '' })
        setDeclineReason('')
    }

    if (loading) return <LoadingMessage text="Loading leave details..." />
    if (!leave) return <LoadingMessage text="Failed to load leave details." isError />

    return (
        <div className="page-container flex justify-center">
            <div className="w-full max-w-3xl card-surface p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b pb-4 mb-4 gap-2">
                    <h2 className="page-title">LEAVE REQUEST FOR {leave.extendedProps?.fullname}</h2>

                    <span
                        className={
                            leave.extendedProps?.status === 'APPROVED'
                                ? 'badge-success'
                                : leave.extendedProps?.status === 'PENDING'
                                  ? 'badge-warning'
                                  : 'badge-neutral'
                        }
                    >
                        {leave.extendedProps?.status || 'N/A'}
                    </span>
                </div>
                {message.isOpen && (
                    <MessageDialog
                        type={message.type}
                        text={message.text}
                        onClose={() => setMessage((prev) => ({ ...prev, isOpen: false }))}
                    />
                )}

                <div className="text-sm border rounded-lg p-3 bg-gray-50 mb-4">
                    <h4 className="font-medium mb-3">Leave Balances:</h4>
                    <div className="flex flex-wrap gap-4 sm:gap-6 items-center text-sm">
                        <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 text-slate-700 font-semibold text-sm">
                                {leaveData.vacationLeave}
                            </div>
                            <span>Vacation Leave</span>
                        </div>

                        <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-red-50 text-red-700 font-semibold text-sm">
                                {leaveData.sickLeave}
                            </div>
                            <span>Sick Leave</span>
                        </div>

                        <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-50 text-amber-700 font-semibold text-sm">
                                {leaveData.emergencyLeave}
                            </div>
                            <span>Emergency Leave</span>
                        </div>
                    </div>
                </div>
                <LeaveDetailsSection leave={leave} />

                <LeaveActions
                    title="Checked By"
                    leave={leave?.checkedBy}
                    verifiedAt={formatDateTime(leave?.verifiedCheckedBy)}
                />
                <LeaveActions
                    title="Approved By"
                    leave={leave?.approvedBy}
                    verifiedAt={formatDateTime(leave?.verifiedApprovedBy)}
                />
                {/* <LeaveActions
                    title="Final Approved By"
                    leave={leave?.lastApprovedBy}
                    verifiedAt={formatDateTime(leave?.verifiedLastApprovedBy)}
                /> */}

                <div className="flex flex-col sm:flex-row justify-center mt-8 gap-3 sm:gap-6">
                    <button className="btn-ghost" onClick={() => setEditModalOpen(true)}>
                        Edit Leave
                    </button>

                    <button
                        className="btn-secondary"
                        onClick={() => setModalState({ isOpen: true, action: 'DECLINED' })}
                    >
                        Decline
                    </button>
                    <button className="btn-primary" onClick={() => handleStatusChange('APPROVED')}>
                        Approve
                    </button>
                </div>

                {modalState.isOpen && (
                    <DeclineModal
                        declineReason={declineReason}
                        setDeclineReason={setDeclineReason}
                        onClose={resetModal}
                        onConfirm={() => handleStatusChange(modalState.action, 'declined')}
                    />
                )}

                <LeaveEditModal
                    open={editModalOpen}
                    onClose={() => setEditModalOpen(false)}
                    leave={leave}
                    onSave={fetchData}
                />
            </div>
        </div>
    )
}

// =============== COMPONENTS =============== //

const LoadingMessage = ({ text, isError = false }) => (
    <p className={`text-center ${isError ? 'text-red-500' : 'text-gray-500'}`}>{text}</p>
)

const MessageDialog = ({ type, text, onClose }) => (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 px-4">
        <div className="card-surface p-6 w-full max-w-md">
            <h3 className={`text-lg font-medium ${type === 'success' ? 'text-emerald-600' : 'text-red-600'}`}>
                {type === 'success' ? 'Success' : 'Error'}
            </h3>
            <p className="mt-2 text-black">{text}</p>
            <div className="flex justify-end mt-4">
                <button className="btn-primary" onClick={onClose}>
                    Close
                </button>
            </div>
        </div>
    </div>
)

const LeaveDetailsSection = ({ leave }) => (
    <div className="text-sm border-b pb-4 mb-4 space-y-2">
        {Object.entries({
            Type: leave.type.replace('_', ' '),
            Reason: leave.reason || 'No description',
            Date: `${leave.start} to ${leave.end}`,
            Duration: leave.extendedProps?.duration || 'N/A',
            'Internal Remarks': leave.internalRemarks || 'N/A',
            Remarks: leave.remarks || 'N/A',
            // Status: leave.extendedProps?.status || 'N/A',
            'Currently Assigned To': leave.assignedUser
                ? `${leave.assignedUser.firstName} ${leave.assignedUser.lastName}`
                : 'N/A'
        }).map(([label, value]) => (
            <p key={label}>
                <strong>{label}:</strong> {value}
            </p>
        ))}
    </div>
)

const LeaveActions = ({ title, leave, verifiedAt }) => (
    <div className="text-sm border-b pb-4 mb-4 space-y-2">
        <h4 className="font-medium">{title}:</h4>

        <p>
            <strong>Name:</strong> {leave ? `${leave.firstName || 'N/A'} ${leave.lastName || 'N/A'}` : 'N/A'}
        </p>

        <p className="text-gray-500 text-sm">
            <strong>Verified:</strong> {verifiedAt}
        </p>
    </div>
)

const DeclineModal = ({ declineReason, setDeclineReason, onClose, onConfirm }) => (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 px-4">
        <div className="card-surface p-6 w-full max-w-md">
            <h3 className="text-lg font-medium">Decline Leave Request</h3>
            <textarea
                className="input-field mt-4"
                placeholder="Enter reason..."
                rows="3"
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
            />
            <div className="flex flex-col sm:flex-row justify-end mt-4 gap-2">
                <button className="btn-secondary" onClick={onClose}>
                    Cancel
                </button>
                <button className="btn-primary" onClick={onConfirm}>
                    Confirm Decline
                </button>
            </div>
        </div>
    </div>
)

const formatDateTime = (dateTime) => (dateTime ? moment(dateTime).format('MMMM DD, YYYY [at] hh:mm A') : 'PENDING')

export default LeaveDetails
